// WMO weather interpretation codes
const WMO_CODES = {
  0:  { label: 'Clear Sky',              icon: '☀️' },
  1:  { label: 'Mostly Clear',           icon: '🌤️' },
  2:  { label: 'Partly Cloudy',          icon: '⛅' },
  3:  { label: 'Overcast',               icon: '☁️' },
  45: { label: 'Foggy',                  icon: '🌫️' },
  48: { label: 'Icy Fog',                icon: '🌫️' },
  51: { label: 'Light Drizzle',          icon: '🌦️' },
  53: { label: 'Drizzle',                icon: '🌦️' },
  55: { label: 'Heavy Drizzle',          icon: '🌧️' },
  61: { label: 'Light Rain',             icon: '🌧️' },
  63: { label: 'Rain',                   icon: '🌧️' },
  65: { label: 'Heavy Rain',             icon: '🌧️' },
  71: { label: 'Light Snow',             icon: '🌨️' },
  73: { label: 'Snow',                   icon: '❄️' },
  75: { label: 'Heavy Snow',             icon: '❄️' },
  77: { label: 'Snow Grains',            icon: '🌨️' },
  80: { label: 'Light Showers',          icon: '🌦️' },
  81: { label: 'Showers',               icon: '🌧️' },
  82: { label: 'Heavy Showers',          icon: '⛈️' },
  85: { label: 'Snow Showers',           icon: '🌨️' },
  86: { label: 'Heavy Snow Showers',     icon: '🌨️' },
  95: { label: 'Thunderstorm',           icon: '⛈️' },
  96: { label: 'Thunderstorm + Hail',    icon: '⛈️' },
  99: { label: 'Severe Thunderstorm',    icon: '⛈️' },
};

export function getWeatherMeta(code) {
  return WMO_CODES[code] ?? { label: 'Unknown', icon: '🌡️' };
}

export function getAqiInfo(aqi) {
  if (aqi == null) return { label: 'N/A', color: '#9ca3af', bg: 'rgba(156,163,175,0.15)' };
  if (aqi <= 50)  return { label: 'Good',                          color: '#22c55e', bg: 'rgba(34,197,94,0.15)' };
  if (aqi <= 100) return { label: 'Moderate',                      color: '#eab308', bg: 'rgba(234,179,8,0.15)' };
  if (aqi <= 150) return { label: 'Unhealthy for Sensitive Groups', color: '#f97316', bg: 'rgba(249,115,22,0.15)' };
  if (aqi <= 200) return { label: 'Unhealthy',                     color: '#ef4444', bg: 'rgba(239,68,68,0.15)' };
  if (aqi <= 300) return { label: 'Very Unhealthy',                color: '#a855f7', bg: 'rgba(168,85,247,0.15)' };
  return           { label: 'Hazardous',                           color: '#7f1d1d', bg: 'rgba(127,29,29,0.15)' };
}

/**
 * Fetches weather + air quality from Open-Meteo (free, no API key).
 * Returns { weather, aqi } raw API response objects.
 */
export async function fetchWeather(lat, lng) {
  const weatherUrl =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lng}` +
    `&current=temperature_2m,apparent_temperature,relative_humidity_2m,precipitation,weather_code,wind_speed_10m,wind_gusts_10m,surface_pressure,cloud_cover` +
    `&daily=temperature_2m_max,temperature_2m_min,apparent_temperature_max,weather_code,precipitation_sum,precipitation_probability_max,rain_sum,snowfall_sum,precipitation_hours,wind_speed_10m_max,wind_gusts_10m_max` +
    `&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch&timezone=auto&forecast_days=7`;

  const aqiUrl =
    `https://air-quality-api.open-meteo.com/v1/air-quality` +
    `?latitude=${lat}&longitude=${lng}` +
    `&current=us_aqi,pm2_5,pm10,carbon_monoxide,nitrogen_dioxide,ozone`;

  const [weatherRes, aqiRes] = await Promise.all([fetch(weatherUrl), fetch(aqiUrl)]);

  if (!weatherRes.ok) throw new Error('Weather fetch failed');
  if (!aqiRes.ok) throw new Error('Air quality fetch failed');

  const [weather, aqi] = await Promise.all([weatherRes.json(), aqiRes.json()]);
  return { weather, aqi };
}

/**
 * RainViewer free Weather Maps API (post Jan 2026):
 *   - past radar frames only (~2 hours, 10-min steps)
 *   - max tile zoom = 7 (Mapbox must set source maxzoom: 7 and overzoom beyond that)
 *   - Universal Blue color scheme (id 2) is the free default
 *
 * Docs: https://www.rainviewer.com/api/weather-maps-api.html
 */
export const RADAR_MAX_ZOOM = 7;

/**
 * Fetches the most recent RainViewer radar frame.
 * Returns { time, host, path } or null.
 */
export async function fetchRadarTimestamp() {
  const res = await fetch('https://api.rainviewer.com/public/weather-maps.json');
  if (!res.ok) throw new Error('RainViewer fetch failed');
  const data = await res.json();
  const frames = data.radar?.past ?? [];
  if (!frames.length) return null;
  const frame = frames[frames.length - 1];
  // Prefer host+path from the API response; fall back to timestamp-only URL.
  return {
    time: frame.time,
    host: (data.host || 'https://tilecache.rainviewer.com').replace(/\/$/, ''),
    path: frame.path || `/v2/radar/${frame.time}`,
  };
}

/**
 * Mapbox/Leaflet XYZ template for a RainViewer frame.
 * size=256, color=2 (Universal Blue), options=0_0 (no smooth / no snow extras).
 * Pass the object from fetchRadarTimestamp(), or a bare unix timestamp for legacy callers.
 */
export function buildRadarTileUrl(frameOrTimestamp) {
  if (frameOrTimestamp && typeof frameOrTimestamp === 'object') {
    const { host, path } = frameOrTimestamp;
    return `${host}${path}/256/{z}/{x}/{y}/2/0_0.png`;
  }
  return `https://tilecache.rainviewer.com/v2/radar/${frameOrTimestamp}/256/{z}/{x}/{y}/2/0_0.png`;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Fetches 1991–2020 climate normals from the Open-Meteo Archive API (free, no key)
 * and aggregates ~30 years of daily observations into:
 *   - monthly: 12 calendar-month normals (temps, precip/rain/snow, heat/wind, hazard proxies)
 *   - byMonthDay: per "MM-DD" normals for week/day drill-downs
 *
 * Results are cached in localStorage on a ~0.1° grid (30-day TTL) because climate
 * normals never change — repeat visits / pan-backs hit the cache instead of the API,
 * which keeps us under Open-Meteo's rate limit.
 *
 * Hazard proxies (for tour routing — not official disaster records):
 *   wetDays        precip ≥ 0.1 in
 *   heavyRainDays  precip ≥ 1.0 in  (flash-flood / washout risk proxy)
 *   snowDays       snowfall ≥ 0.4 in
 *   heatDays       apparent high ≥ 95°F
 *   stormDays      WMO code ≥ 95 (thunderstorm)
 */
const CLIMATE_CACHE_PREFIX = 'vrc:climate:v2:';
const CLIMATE_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const CLIMATE_GRID = 1; // decimal places for grid key (0.1° ≈ 11 km)
const WET_DAY_IN = 0.1;
const HEAVY_RAIN_IN = 1.0;
const SNOW_DAY_IN = 0.4;
const HEAT_DAY_F = 95;

function climateCacheKey(lat, lng) {
  return `${CLIMATE_CACHE_PREFIX}${lat.toFixed(CLIMATE_GRID)},${lng.toFixed(CLIMATE_GRID)}`;
}

function readClimateCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > CLIMATE_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch { return null; }
}

function writeClimateCache(key, data) {
  try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch { /* quota */ }
}

function emptyClimateBucket() {
  return {
    highSum: 0, highN: 0,
    lowSum: 0, lowN: 0,
    feelsHighSum: 0, feelsHighN: 0,
    precipSum: 0, precipN: 0,
    rainSum: 0, rainN: 0,
    snowSum: 0, snowN: 0,
    precipHoursSum: 0, precipHoursN: 0,
    windMaxSum: 0, windMaxN: 0,
    wetDays: 0,
    heavyRainDays: 0,
    snowDays: 0,
    heatDays: 0,
    stormDays: 0,
    years: 0,
  };
}

function accumulateClimateDay(bucket, {
  tmax, tmin, feels, precip, rain, snow, precipHours, windMax, weatherCode,
}) {
  if (typeof tmax === 'number') { bucket.highSum += tmax; bucket.highN += 1; }
  if (typeof tmin === 'number') { bucket.lowSum += tmin; bucket.lowN += 1; }
  if (typeof feels === 'number') { bucket.feelsHighSum += feels; bucket.feelsHighN += 1; }
  if (typeof precip === 'number') {
    bucket.precipSum += precip;
    bucket.precipN += 1;
    if (precip >= WET_DAY_IN) bucket.wetDays += 1;
    if (precip >= HEAVY_RAIN_IN) bucket.heavyRainDays += 1;
  }
  if (typeof rain === 'number') { bucket.rainSum += rain; bucket.rainN += 1; }
  if (typeof snow === 'number') {
    bucket.snowSum += snow;
    bucket.snowN += 1;
    if (snow >= SNOW_DAY_IN) bucket.snowDays += 1;
  }
  if (typeof precipHours === 'number') { bucket.precipHoursSum += precipHours; bucket.precipHoursN += 1; }
  if (typeof windMax === 'number') { bucket.windMaxSum += windMax; bucket.windMaxN += 1; }
  if (typeof feels === 'number' && feels >= HEAT_DAY_F) bucket.heatDays += 1;
  if (typeof weatherCode === 'number' && weatherCode >= 95) bucket.stormDays += 1;
}

function finalizeClimateBucket(bucket, { monthly = false } = {}) {
  // Monthly precip is total average monthly sum (30-year mean of monthly totals).
  // Day buckets use mean daily amount across years.
  const years = 30;
  return {
    high: bucket.highN ? Math.round((bucket.highSum / bucket.highN) * 10) / 10 : null,
    low: bucket.lowN ? Math.round((bucket.lowSum / bucket.lowN) * 10) / 10 : null,
    feelsHigh: bucket.feelsHighN ? Math.round((bucket.feelsHighSum / bucket.feelsHighN) * 10) / 10 : null,
    precip: bucket.precipN
      ? Math.round((monthly ? bucket.precipSum / years : bucket.precipSum / years) * (monthly ? 10 : 100)) / (monthly ? 10 : 100)
      : null,
    rain: bucket.rainN
      ? Math.round((monthly ? bucket.rainSum / years : bucket.rainSum / years) * (monthly ? 10 : 100)) / (monthly ? 10 : 100)
      : null,
    snow: bucket.snowN
      ? Math.round((monthly ? bucket.snowSum / years : bucket.snowSum / years) * (monthly ? 10 : 100)) / (monthly ? 10 : 100)
      : null,
    precipHours: bucket.precipHoursN
      ? Math.round((bucket.precipHoursSum / (monthly ? years : years)) * 10) / 10
      : null,
    windMax: bucket.windMaxN ? Math.round((bucket.windMaxSum / bucket.windMaxN) * 10) / 10 : null,
    // Average days-per-month (or probability for a calendar day × 30 ≈ count for month context)
    wetDays: Math.round((bucket.wetDays / years) * 10) / 10,
    heavyRainDays: Math.round((bucket.heavyRainDays / years) * 10) / 10,
    snowDays: Math.round((bucket.snowDays / years) * 10) / 10,
    heatDays: Math.round((bucket.heatDays / years) * 10) / 10,
    stormDays: Math.round((bucket.stormDays / years) * 10) / 10,
  };
}

export async function fetchClimateNormals(lat, lng) {
  // Snap to grid so nearby coords share a cache entry and a single API call.
  const gridLat = Number(lat.toFixed(CLIMATE_GRID));
  const gridLng = Number(lng.toFixed(CLIMATE_GRID));
  const cacheKey = climateCacheKey(gridLat, gridLng);
  const cached = readClimateCache(cacheKey);
  if (cached) return cached;

  const url =
    `https://archive-api.open-meteo.com/v1/archive` +
    `?latitude=${gridLat}&longitude=${gridLng}` +
    `&start_date=1991-01-01&end_date=2020-12-31` +
    `&daily=temperature_2m_max,temperature_2m_min,apparent_temperature_max,precipitation_sum,rain_sum,snowfall_sum,precipitation_hours,wind_speed_10m_max,weather_code` +
    `&timezone=auto&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch`;

  const res = await fetch(url);
  if (!res.ok) throw new Error('Climate normals fetch failed');
  const data = await res.json();
  const times = data.daily?.time || [];
  const tmax = data.daily?.temperature_2m_max || [];
  const tmin = data.daily?.temperature_2m_min || [];
  const feels = data.daily?.apparent_temperature_max || [];
  const precip = data.daily?.precipitation_sum || [];
  const rain = data.daily?.rain_sum || [];
  const snow = data.daily?.snowfall_sum || [];
  const precipHours = data.daily?.precipitation_hours || [];
  const windMax = data.daily?.wind_speed_10m_max || [];
  const weatherCode = data.daily?.weather_code || [];

  const monthBuckets = Array.from({ length: 12 }, () => emptyClimateBucket());
  const dayBuckets = {}; // "MM-DD" -> accumulator
  for (let i = 0; i < times.length; i++) {
    const d = new Date(times[i] + 'T12:00:00');
    const m = d.getMonth();
    const key = `${String(m + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!dayBuckets[key]) dayBuckets[key] = emptyClimateBucket();
    const sample = {
      tmax: tmax[i],
      tmin: tmin[i],
      feels: feels[i],
      precip: precip[i],
      rain: rain[i],
      snow: snow[i],
      precipHours: precipHours[i],
      windMax: windMax[i],
      weatherCode: weatherCode[i],
    };
    accumulateClimateDay(monthBuckets[m], sample);
    accumulateClimateDay(dayBuckets[key], sample);
  }

  const monthly = monthBuckets.map((b, monthIndex) => ({
    month: MONTH_NAMES[monthIndex],
    monthIndex,
    ...finalizeClimateBucket(b, { monthly: true }),
  }));

  const byMonthDay = {};
  for (const [key, b] of Object.entries(dayBuckets)) {
    byMonthDay[key] = {
      key,
      ...finalizeClimateBucket(b, { monthly: false }),
    };
  }

  const result = {
    monthly,
    byMonthDay,
    units: {
      high: '°F',
      low: '°F',
      feelsHigh: '°F',
      precip: 'in',
      rain: 'in',
      snow: 'in',
      precipHours: 'h',
      windMax: 'mph',
      wetDays: 'days/mo',
      heavyRainDays: 'days/mo',
      snowDays: 'days/mo',
      heatDays: 'days/mo',
      stormDays: 'days/mo',
    },
    thresholds: {
      wetDayIn: WET_DAY_IN,
      heavyRainIn: HEAVY_RAIN_IN,
      snowDayIn: SNOW_DAY_IN,
      heatDayF: HEAT_DAY_F,
    },
  };
  writeClimateCache(cacheKey, result);
  return result;
}

/** Build short tour-planning notes from a monthly normals row. */
export function getTourClimateNotes(monthRow, thresholds = {}) {
  if (!monthRow) return [];
  const heavyRainIn = thresholds.heavyRainIn ?? HEAVY_RAIN_IN;
  const notes = [];
  if ((monthRow.heavyRainDays || 0) >= 1.5) {
    notes.push({
      id: 'flood',
      tone: 'warn',
      text: `Heavy rain common (~${monthRow.heavyRainDays} days ≥ ${heavyRainIn}" ). Flash-flood / washout risk — consider alternate routes & waterproof gear.`,
    });
  } else if ((monthRow.wetDays || 0) >= 10 || (monthRow.precip || 0) >= 3) {
    notes.push({
      id: 'rain',
      tone: 'info',
      text: `Wet month: ~${monthRow.wetDays} rainy days, ~${(monthRow.precip ?? 0).toFixed(1)}" typical precip.`,
    });
  }
  if ((monthRow.snowDays || 0) >= 2 || (monthRow.snow || 0) >= 3) {
    notes.push({
      id: 'snow',
      tone: 'warn',
      text: `Snow season: ~${monthRow.snowDays} snow days / ~${(monthRow.snow ?? 0).toFixed(1)}" typical. Plan for delays & winter driving.`,
    });
  }
  if ((monthRow.heatDays || 0) >= 5 || (monthRow.feelsHigh || 0) >= 95) {
    notes.push({
      id: 'heat',
      tone: 'warn',
      text: `Heat stress: feels-like highs often ~${Math.round(monthRow.feelsHigh ?? 0)}°F (~${monthRow.heatDays} hot days). Hydration & shade matter for load-in.`,
    });
  }
  if ((monthRow.stormDays || 0) >= 2) {
    notes.push({
      id: 'storm',
      tone: 'warn',
      text: `Thunderstorms average ~${monthRow.stormDays} days this month. Watch outdoor load-in / festival timing.`,
    });
  }
  if ((monthRow.windMax || 0) >= 25) {
    notes.push({
      id: 'wind',
      tone: 'info',
      text: `Gusty: typical max winds ~${Math.round(monthRow.windMax)} mph.`,
    });
  }
  return notes;
}

const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function pad2(n) { return String(n).padStart(2, '0'); }

/** Returns Monday-based start of week for a Date. */
export function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = (day + 6) % 7; // back to Monday
  d.setDate(d.getDate() - diff);
  return d;
}

/** Normal for a specific calendar date, keyed by "MM-DD". */
export function getNormalForDate(date, climateNormals) {
  const key = `${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  return climateNormals?.byMonthDay?.[key] ?? null;
}

/** Build a 7-day week trend starting at the Monday on/before `refDate`. */
export function getWeekTrend(refDate, climateNormals) {
  const start = startOfWeek(refDate);
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push({
      date: d,
      label: DAY_SHORT[d.getDay()],
      normal: getNormalForDate(d, climateNormals),
    });
  }
  return out;
}

/** Compare a forecast/actual high to a normal row (from byMonthDay or monthly). */
export function compareToNormal(forecastHigh, normalRow) {
  if (!normalRow || forecastHigh == null || normalRow.high == null) return null;
  const delta = Math.round(forecastHigh - normalRow.high);
  if (Math.abs(delta) < 2) return { label: 'Near typical', delta, typicalHigh: normalRow.high, typicalLow: normalRow.low };
  if (delta > 0) return { label: `${delta}° warmer than typical`, delta, typicalHigh: normalRow.high, typicalLow: normalRow.low };
  return { label: `${Math.abs(delta)}° cooler than typical`, delta, typicalHigh: normalRow.high, typicalLow: normalRow.low };
}

/** Back-compat wrapper: compare against the monthly normal for a month index. */
export function compareToClimateNormal(forecastHigh, monthIndex, climateNormals) {
  const row = climateNormals?.monthly?.find((m) => m.monthIndex === monthIndex);
  return compareToNormal(forecastHigh, row);
}

export { DAY_FULL, DAY_SHORT };
